import type {ChatInputRef} from '@github-ui/copilot-chat/components/ChatInput'
import {type AvailableCommand, useAvailableCommands} from '@github-ui/copilot-chat/hooks/use-available-commands'
import {extractChatCommandType} from '@github-ui/copilot-chat/utils/chat-commands-helpers'
import type {CopilotChatCommandType, StarterEntry} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useNavigate} from '@github-ui/use-navigate'

import {CommandStarters} from './CommandStarters'
import {CommandSuggestions} from './CommandSuggestions'

interface CommandStartersAndSuggestionsProps {
  chatCommand: CopilotChatCommandType | null
  chatInputRef: React.RefObject<ChatInputRef | null>
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>
  containerRef: React.RefObject<HTMLElement | null>
  iceBreakersExperimentEnabled?: boolean
  paidUser?: boolean
  activeModeName?: string
}

export function CommandStartersAndSuggestions({
  chatCommand,
  chatInputRef,
  containerRef,
  textAreaRef,
  iceBreakersExperimentEnabled,
  paidUser,
  activeModeName,
}: CommandStartersAndSuggestionsProps) {
  const {availableCommands, availableCommandKeys} = useAvailableCommands({enabled: true})
  const navigate = useNavigate()

  const handleCommandSelect = (command: AvailableCommand) => {
    sendEvent('dotcom_chat.activate', {target: 'COMMAND_STARTER_SELECTED', mode: 'immersive', command: command.key})
    if (command.enabled?.() === false && command.disabledAction) {
      command.disabledAction(navigate)
      return
    }
    chatInputRef.current?.setText(text => {
      // Replace the existing command with the new one
      const foundCommand = extractChatCommandType(text, availableCommandKeys)

      if (foundCommand) {
        return text.replace(`/${foundCommand}`, `/${command.key}`)
      }

      return `/${command.key} ${text}`
    })

    textAreaRef.current?.focus()
  }

  const handleSuggestionSelect = (command: CopilotChatCommandType, suggestion: string) => {
    sendEvent('dotcom_chat.activate', {
      target: 'COMMAND_SUGGESTION_SELECTED',
      mode: 'immersive',
      command,
      suggestion,
      iceBreakerFFEnabled: iceBreakersExperimentEnabled,
    })
    chatInputRef.current?.setText(`/${command} ${suggestion}`.trim())
    textAreaRef.current?.focus()
  }

  const handleIceBreakerSelect = (command: StarterEntry) => {
    sendEvent('dotcom_chat.activate', {
      target: 'COMMAND_SUGGESTION_SELECTED',
      mode: 'immersive',
      command: command.id,
      suggestion: command.message,
      iceBreakerFFEnabled: iceBreakersExperimentEnabled,
    })
    chatInputRef.current?.setText(`${command.message}`.trim())
    textAreaRef.current?.focus()
  }

  const suggestions = chatCommand ? availableCommands.find(command => command.key === chatCommand)?.suggestions : null
  const hasSuggestions = suggestions && suggestions.length > 0

  return (
    <>
      {/* only show suggestions if they exist, otherwise default to command starters */}
      {chatCommand && hasSuggestions && (
        <CommandSuggestions
          key={chatCommand}
          chatCommand={chatCommand}
          suggestions={suggestions}
          onSelect={handleSuggestionSelect}
          anchorRef={containerRef}
          textAreaRef={textAreaRef}
        />
      )}
      <>
        <h2 className="sr-only">Chat Commands</h2>
        <CommandStarters
          onSelect={handleCommandSelect}
          onIceBreakerSelect={handleIceBreakerSelect}
          iceBreakersExperimentEnabled={iceBreakersExperimentEnabled}
          paidUser={paidUser}
          activeModeName={activeModeName}
        />
      </>
    </>
  )
}
